/**
 * Top utility bar shown above the main navigation.
 *
 * Desktop  → 4 trust badges side-by-side, separated by vertical dividers
 *            (static — all four visible at once).
 * Mobile   → single badge that auto-rotates through BADGES every ROTATE_MS,
 *            with a short fade transition so the change is visible. No
 *            manual arrows, no pagination dots (per design feedback).
 */
import React, { useEffect, useRef, useState } from 'react';
import { buildWhatsAppUrl } from '../../utils/whatsappOrder';

const ROTATE_MS = 3500;
const FADE_MS = 250;

const BADGES = [
  {
    label: 'Envío a todo Argentina',
    icon: (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <rect x="1" y="4" width="15" height="13" rx="1" />
        <polygon points="16 8 20 8 23 11 23 17 16 17 16 8" />
        <circle cx="5.5" cy="18.5" r="2.5" />
        <circle cx="18.5" cy="18.5" r="2.5" />
      </svg>
    ),
  },
  {
    label: '6 cuotas sin interés',
    icon: (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <rect x="1" y="4" width="22" height="16" rx="2" />
        <line x1="1" y1="10" x2="23" y2="10" />
      </svg>
    ),
  },
  {
    label: 'Asesoría por WhatsApp',
    href: buildWhatsAppUrl('Hola Pinksisly, quisiera asesoría de estilismo.'),
    icon: (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" />
      </svg>
    ),
  },
];

const arrowBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 22,
  height: 22,
  padding: 0,
  background: 'transparent',
  border: 'none',
  color: 'inherit',
  opacity: 0.75,
  cursor: 'pointer',
  flexShrink: 0,
};

const BadgeContent = ({ badge }) => {
  const inner = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <span style={{ opacity: 0.85, display: 'inline-flex' }}>{badge.icon}</span>
      <span>{badge.label}</span>
    </span>
  );
  if (badge.href) {
    return (
      <a
        href={badge.href}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: 'inherit', textDecoration: 'none' }}
      >
        {inner}
      </a>
    );
  }
  return inner;
};

const TopBanner = () => {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  // pauseRef lets pointer/touch interaction freeze auto-rotation without
  // tearing down the interval — far less janky than clearing/restarting it.
  const pauseRef = useRef(false);

  // Auto-rotate the mobile message. Brief fade-out → swap → fade-in so the
  // user can perceive that the message is changing.
  useEffect(() => {
    const id = setInterval(() => {
      if (pauseRef.current) return;
      setVisible(false);
      setTimeout(() => {
        setIndex((i) => (i + 1) % BADGES.length);
        setVisible(true);
      }, FADE_MS);
    }, ROTATE_MS);
    return () => clearInterval(id);
  }, []);

  // Manual navigation — also runs the fade so the swap looks the same as
  // the auto-rotation tick.
  const goTo = (next) => {
    setVisible(false);
    setTimeout(() => {
      setIndex(((next % BADGES.length) + BADGES.length) % BADGES.length);
      setVisible(true);
    }, FADE_MS);
  };
  const goPrev = () => goTo(index - 1);
  const goNext = () => goTo(index + 1);
  const pause = () => {
    pauseRef.current = true;
  };
  const resume = () => {
    pauseRef.current = false;
  };

  return (
    // SS pass (2026-08): soft-pink band (brand `accent-soft`) with dark ink
    // text (~10.5:1 contrast) instead of the old black bar — lightens the
    // first impression for the summer season.
    <div
      style={{
        background: '#F4C7C3',
        color: '#2A1F1A',
        width: '100%',
        position: 'relative',
        zIndex: 41,
        fontFamily: 'var(--font-body, system-ui)',
      }}
    >
      {/* DESKTOP: 4-up static grid (md and up).
          NOTE: do NOT set inline `display: 'flex'` here — it overrides
          Tailwind's `hidden` class and the desktop row would render even
          on mobile (double-banner bug). Tailwind owns visibility. */}
      <div
        className="hidden md:flex"
        style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '12px 24px',
          alignItems: 'center',
          justifyContent: 'space-around',
          gap: 16,
          fontSize: 12.5,
          letterSpacing: '0.01em',
        }}
      >
        {BADGES.map((b, i) => (
          <React.Fragment key={i}>
            <BadgeContent badge={b} />
            {i < BADGES.length - 1 && (
              <span
                style={{ width: 1, height: 18, background: 'rgba(42,31,26,0.18)' }}
                aria-hidden="true"
              />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* MOBILE: auto-rotating badge with manual prev/next arrows and
          pagination dots. Pointer/touch pauses rotation so the user can
          finish reading without the message swapping out from under them.
          IMPORTANT: do NOT add inline `display: 'flex'` here — it would
          override Tailwind's `md:hidden` and double-render on desktop. */}
      <div
        className="flex md:hidden"
        style={{
          flexDirection: 'column',
          alignItems: 'center',
          padding: '8px 10px 6px',
          fontSize: 12.5,
          gap: 4,
        }}
        onMouseEnter={pause}
        onMouseLeave={resume}
        onTouchStart={pause}
        onTouchEnd={resume}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            gap: 6,
            minHeight: 28,
          }}
        >
          <button type="button" onClick={goPrev} aria-label="Anterior" style={arrowBtn}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <div style={{ flex: 1, display: 'flex', justifyContent: 'center', minWidth: 0 }}>
            <span
              style={{
                opacity: visible ? 1 : 0,
                transition: `opacity ${FADE_MS}ms ease`,
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              <BadgeContent badge={BADGES[index]} />
            </span>
          </div>
          <button type="button" onClick={goNext} aria-label="Siguiente" style={arrowBtn}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
        <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
          {BADGES.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => goTo(i)}
              aria-label={`Mostrar mensaje ${i + 1}`}
              style={{
                width: i === index ? 12 : 5,
                height: 5,
                borderRadius: 999,
                padding: 0,
                border: 'none',
                background: i === index ? '#2A1F1A' : 'rgba(42,31,26,0.30)',
                cursor: 'pointer',
                transition: 'width 0.18s ease, background 0.18s ease',
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default TopBanner;
